import { useState } from "react";

import { PasswordField } from "@/components/auth/PasswordField";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api, getErrorMessage } from "@/lib/api";


export default function AdminSettingsPage() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [status, setStatus] = useState({ loading: false, error: "", success: "" });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ loading: true, error: "", success: "" });

    if (form.new_password !== form.confirm_password) {
      setStatus({ loading: false, error: "New password and confirmation do not match.", success: "" });
      return;
    }

    if (form.new_password.length < 10) {
      setStatus({ loading: false, error: "New password must be at least 10 characters long.", success: "" });
      return;
    }

    try {
      const response = await api.post("/auth/admin/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setForm({ current_password: "", new_password: "", confirm_password: "" });
      setStatus({ loading: false, error: "", success: response.data.message || "Password updated successfully." });
    } catch (error) {
      setStatus({ loading: false, error: getErrorMessage(error, "We could not update the password."), success: "" });
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-settings-page">
      {status.error && <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-settings-error-message">{status.error}</p>}
      {status.success && <p className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" data-testid="admin-settings-success-message">{status.success}</p>}

      <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="admin-settings-overview-card">
        <CardContent className="flex flex-wrap items-end justify-between gap-4 p-8">
          <div className="space-y-3">
            <p className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--brand-primary-orange, #F5A623)" }} data-testid="admin-settings-eyebrow">Super admin settings</p>
            <h2 className="text-[2.2rem] font-semibold leading-[1.08] tracking-[-0.025em] text-slate-950" data-testid="admin-settings-title">Password management</h2>
            <p className="text-[16px] leading-8 text-slate-600" data-testid="admin-settings-description">Update the active super admin password with backend validation and audit-safe event logging.</p>
          </div>
          <p className="text-sm leading-7 text-slate-600" data-testid="admin-settings-note-text">Your current password is required before any change is accepted.</p>
        </CardContent>
      </Card>

      <Card className="border-stone-200/80 bg-white/92 shadow-none" data-testid="admin-settings-password-card">
        <CardHeader>
          <CardTitle className="text-[1.8rem] font-semibold leading-tight tracking-[-0.02em] text-slate-950" data-testid="admin-settings-password-title">Change super admin password</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="max-w-2xl space-y-5" onSubmit={handleSubmit} data-testid="admin-settings-password-form">
            <PasswordField placeholder="Current password" value={form.current_password} onChange={(event) => setForm((current) => ({ ...current, current_password: event.target.value }))} data-testid="admin-settings-current-password-input" />
            <PasswordField placeholder="New password" value={form.new_password} onChange={(event) => setForm((current) => ({ ...current, new_password: event.target.value }))} data-testid="admin-settings-new-password-input" />
            <PasswordField placeholder="Confirm new password" value={form.confirm_password} onChange={(event) => setForm((current) => ({ ...current, confirm_password: event.target.value }))} data-testid="admin-settings-confirm-password-input" />
            <p className="text-sm leading-7 text-slate-600" data-testid="admin-settings-password-help-text">Use at least 10 characters. The new password must differ from the current password.</p>
            <Button type="submit" className="rounded-full" style={{ backgroundColor: "var(--brand-primary-dark, #2B2B2B)", color: "var(--brand-white, #FFFFFF)" }} disabled={status.loading} data-testid="admin-settings-submit-button">{status.loading ? "Updating password..." : "Update password"}</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
